import { useState } from 'react';
import { useIntl, type MessageDescriptor } from 'react-intl';

import { useLocation } from '@xing-com/crate-core-router';
import {
  addPrefixUrl,
  getCSRFToken,
  getMainHost,
} from '@xing-com/crate-login-utils';
import { useHost, useFetch, useLoginAppUrl } from '@xing-com/crate-xinglet';

import { messages } from './messages';
import { validateLogin } from './utils';

const JSON_HEADERS = {
  'Content-Type': 'application/json; charset=utf-8',
  Accept: 'application/json',
};

function useValidate(
  validateFn: (
    values: Record<string, string>
  ) => Record<string, MessageDescriptor>
): {
  validate: (values: Record<string, string>) => boolean;
  errors: Record<string, MessageDescriptor>;
} {
  const [errors, setErrors] = useState<Record<string, MessageDescriptor>>({});

  const validate = (values: Record<string, string>): boolean => {
    const validationErrors = validateFn(values);

    setErrors(validationErrors);

    return Object.keys(validationErrors).length === 0;
  };

  return { validate, errors };
}

export function useCreateLogin(): {
  execute: (values: Record<string, string>) => Promise<void>;
  error: string;
  validationErrors: Record<string, MessageDescriptor>;
  loading: boolean;
} {
  const intl = useIntl();
  const host = useLoginAppUrl().replace(/\/$/, '');
  const fetch = useFetch();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { validate, errors: validationErrors } = useValidate(validateLogin);

  const execute = async (values: Record<string, string>): Promise<void> => {
    if (!validate(values)) {
      return;
    }

    setLoading(true);

    try {
      const data = await fetch(`${host}/login/api/login`, {
        headers: JSON_HEADERS,
        method: 'POST',
        mode: 'cors',
        credentials: 'include',
        body: JSON.stringify(values),
      });

      const parsedData = await data.json();

      if (!parsedData.success) {
        setLoading(false);
        setError(
          parsedData.description ||
            intl.formatMessage(messages.submitRequestError)
        );
      } else {
        // Handles pong as POST instead of a GET
        if (parsedData.auth_token) {
          const dataPong = await fetch(`${parsedData.target}`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json; charset=utf-8',
              Accept: 'application/json',
              'x-csrf-token': getCSRFToken(),
            },
            mode: 'cors',
            credentials: 'include',
            body: JSON.stringify({
              auth_token: parsedData.auth_token,
              logged_out_sid: parsedData.logged_out_sid,
            }),
          });

          const parsedDataPong = await dataPong.json();

          if (parsedDataPong.success) {
            window.location.href = parsedDataPong.target;
          }
        } else {
          window.location.href = parsedData.target;
        }
      }
    } catch {
      setLoading(false);
      setError(intl.formatMessage(messages.submitRequestError));
    }
  };

  return { execute, error, loading, validationErrors };
}

export function useCreateOnlyfySignup(): {
  execute: (values: Record<string, string>) => Promise<void>;
  error: boolean;
  loading: boolean;
} {
  const hostName = useHost().getHostname();
  const mainHost = addPrefixUrl(getMainHost(hostName), hostName);
  const fetch = useFetch();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  const execute = async (values: Record<string, string>): Promise<void> => {
    setLoading(true);

    try {
      const data = await fetch(`${mainHost}/signup/oauth/onlyfy`, {
        headers: JSON_HEADERS,
        method: 'POST',
        mode: 'cors',
        credentials: 'include',
        body: JSON.stringify(values),
      });

      const parsedData = await data.json();

      if (!parsedData.success || !parsedData.success.login_url) {
        setError(true);
        setLoading(false);
      } else {
        window.location.href = parsedData.success.login_url;
      }
    } catch {
      setLoading(false);
      setError(true);
    }
  };

  return { execute, error, loading };
}

export function useIsLoginDomain(): boolean {
  const { host } = useLocation();
  const loginHost = useLoginAppUrl().replace(/\/$/, '');

  return host === loginHost;
}
